import { z } from "zod"

// Zod schemas for runtime validation

export const LeaderboardEntrySchema = z.object({
	eoa: z.string(),
	peerId: z.string(),
	peerName: z.string(),
	reward: z.number(),
	score: z.number(),
	online: z.boolean(),
})

export const LeaderboardSchema = z.object({
	entries: z.array(LeaderboardEntrySchema),
})

export const TopRewardEntrySchema = z.object({
	id: z.string(),
	name: z.string(),
	reward: z.number(),
})

export const TopRewardsSchema = z.object({
	entries: z.array(TopRewardEntrySchema),
})

export const GossipMessageEntrySchema = z.object({
	id: z.string(),
	peerId: z.string(),
	peerName: z.string(),
	message: z.string(),
	timestamp: z.string(),
})

export const GossipMessagesSchema = z.object({
	messages: z.array(GossipMessageEntrySchema),
})

export const NodesConnectedSchema = z.object({
	count: z.number().int().nonnegative(),
})

export const RoundStageSchema = z.object({
	round: z.number().int().nonnegative(),
	stage: z.number().int().nonnegative(),
})

export const UniqueVotersSchema = z.object({
	count: z.number().int().nonnegative(),
})

export const GetPeerResultSchema = z.object({
	peerId: z.string(),
	peerName: z.string(),
	reward: z.number(),
	online: z.boolean(),
	score: z.number(),
})

export const LoginResponseSchema = z.object({
	csrfToken: z.string(),
})

export const GetPeersByEoaResponseSchema = z.object({
	peers: z.array(
		z.object({
			id: z.string(),
			name: z.string(),
			participation: z.number(),
			reward: z.number(),
			rank: z.number(),
			lastUpdate: z.number(),
		}),
	),
})

export const WhoamiResponseSchema = z.object({
	eoa: z.string(),
	csrfToken: z.string(),
})

export type Leaderboard = z.infer<typeof LeaderboardSchema>
export type LeaderboardEntry = z.infer<typeof LeaderboardEntrySchema>

export type Gossip = z.infer<typeof GossipMessagesSchema>
export type GossipEntry = z.infer<typeof GossipMessageEntrySchema>

export type NodesConnected = z.infer<typeof NodesConnectedSchema>

export type UniqueVoters = z.infer<typeof UniqueVotersSchema>

export type RoundStage = z.infer<typeof RoundStageSchema>

export type TopRewards = z.infer<typeof TopRewardsSchema>
export type TopRewardEntry = z.infer<typeof TopRewardEntrySchema>

export type GetPeerResult = z.infer<typeof GetPeerResultSchema>

export type GetPeersByEoaResponse = z.infer<typeof GetPeersByEoaResponseSchema>
export type LoginResponse = z.infer<typeof LoginResponseSchema>

export type WhoamiResponse = z.infer<typeof WhoamiResponseSchema>

class SwarmApiV1 {
	private readonly baseUrl: string
	private csrfToken: string | null = null

	constructor(baseUrl: string) {
		this.baseUrl = baseUrl
		this.setCsrfToken = this.setCsrfToken.bind(this)
	}

	private setCsrfToken(csrfToken: string | null) {
		this.csrfToken = csrfToken
	}

	/**
	 * Get the number of nodes currently connected
	 * @returns Promise with the count of connected nodes
	 */
	async getNodesConnected(): Promise<z.infer<typeof NodesConnectedSchema>> {
		const response = await fetch(`${this.baseUrl}/nodes-connected`)
		if (!response.ok) {
			throw new Error(`Failed to get nodes connected: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, NodesConnectedSchema)
	}

	/**
	 * Get the current round and stage
	 * @returns Promise with the current round and stage
	 */
	async getRoundStage(): Promise<z.infer<typeof RoundStageSchema>> {
		const response = await fetch(`${this.baseUrl}/round-stage`)
		if (!response.ok) {
			throw new Error(`Failed to get round stage: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, RoundStageSchema)
	}

	/**
	 * Get the top rewards
	 * @returns Promise with the top rewards entries
	 */
	async getTopRewards(): Promise<z.infer<typeof TopRewardsSchema>> {
		const response = await fetch(`${this.baseUrl}/top-rewards`)
		if (!response.ok) {
			throw new Error(`Failed to get top rewards: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, TopRewardsSchema)
	}

	/**
	 * Get the leaderboard
	 * @returns Promise with the leaderboard entries
	 */
	async getLeaderboard(): Promise<z.infer<typeof LeaderboardSchema>> {
		const response = await fetch(`${this.baseUrl}/leaderboard`)
		if (!response.ok) {
			throw new Error(`Failed to get leaderboard: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, LeaderboardSchema)
	}

	/**
	 * Get the number of unique voters
	 * @returns Promise with the count of unique voters
	 */
	async getUniqueVoters(): Promise<z.infer<typeof UniqueVotersSchema>> {
		const response = await fetch(`${this.baseUrl}/unique-voters`)
		if (!response.ok) {
			throw new Error(`Failed to get unique voters: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, UniqueVotersSchema)
	}

	/**
	 * Get gossip messages
	 * @returns Promise with the gossip messages
	 */
	async getGossipMessages(): Promise<z.infer<typeof GossipMessagesSchema>> {
		const response = await fetch(`${this.baseUrl}/gossip-messages`)
		if (!response.ok) {
			throw new Error(`Failed to get gossip messages: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, GossipMessagesSchema)
	}

	async getPeerByName(name: string): Promise<z.infer<typeof GetPeerResultSchema> | null> {
		const response = await fetch(`${this.baseUrl}/peer?name=${name}`)
		if (response.status === 404) {
			return null
		}
		if (!response.ok) {
			throw new Error(`Failed to get peer by name: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, GetPeerResultSchema)
	}

	async getPeerById(id: string): Promise<z.infer<typeof GetPeerResultSchema> | null> {
		const response = await fetch(`${this.baseUrl}/peer?id=${id}`)
		if (response.status === 404) {
			return null
		}
		if (!response.ok) {
			throw new Error(`Failed to get peer by id: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, GetPeerResultSchema)
	}

	async login(eoa: string): Promise<LoginResponse> {
		const response = await fetch(`${this.baseUrl}/login`, {
			credentials: "include",
			method: "POST",
			body: JSON.stringify({ eoa }),
		})
		if (!response.ok) {
			throw new Error(`Failed to login: ${response.statusText}`)
		}

		let parsedData = null

		try {
			const data = await response.json()
			parsedData = this.validateResponse(data, LoginResponseSchema)
		} catch (error) {
			throw error
		}

		this.setCsrfToken(parsedData.csrfToken)
		return parsedData
	}

	async logout(): Promise<void> {
		if (!this.csrfToken) {
			throw new Error("could not logout: CSRF token not found")
		}

		const response = await fetch(`${this.baseUrl}/logout`, {
			credentials: "include",
			method: "POST",
			headers: {
				"X-CSRF-Token": this.csrfToken || "",
			},
		})
		if (!response.ok) {
			throw new Error(`Failed to logout: ${response.statusText}`)
		}

		this.setCsrfToken(null)

		return
	}

	async getPeersForUser(): Promise<z.infer<typeof GetPeersByEoaResponseSchema>> {
		const response = await fetch(`${this.baseUrl}/current-user-peers`, {
			credentials: "include",
		})
		if (!response.ok) {
			throw new Error(`Failed to get peers for user: ${response.statusText}`)
		}
		const data = await response.json()
		return this.validateResponse(data, GetPeersByEoaResponseSchema)
	}

	async whoami(): Promise<z.infer<typeof WhoamiResponseSchema>> {
		const response = await fetch(`${this.baseUrl}/whoami`, {
			credentials: "include",
		})
		if (!response.ok) {
			throw new Error(`Failed to get whoami: ${response.statusText}`)
		}

		let parsedData = null
		try {
			const data = await response.json()
			parsedData = this.validateResponse(data, WhoamiResponseSchema)
		} catch (error) {
			throw error
		}

		this.setCsrfToken(parsedData.csrfToken)
		return parsedData
	}

	/**
	 * Validates API response data against a Zod schema
	 * @param data The response data to validate
	 * @param schema The Zod schema to validate against
	 * @returns The validated data
	 * @throws Error if validation fails
	 */
	private validateResponse<T>(data: unknown, schema: z.ZodType<T>): T {
		try {
			return schema.parse(data)
		} catch (error) {
			if (error instanceof z.ZodError) {
				console.error("API response validation failed:", error.errors)
				throw new Error(`API response validation failed: ${error.message}`)
			}
			throw error
		}
	}
}

const instance = new SwarmApiV1(import.meta.env.VITE_API_BASE_URL)

export default instance
